<template>
  <div
    :class="['content-container']"
    ref="contentContainerTgt"
    :style="{
      '--font-multiplier': String(fontMultiplier),
      ...(isShrinkTarget ? { 'min-height': '0px' } : {}),
    }"
    :dir="isRightToLeft ? 'rtl' : 'ltr'"
  >
    <GeneralMessageChatText
      v-for="(messageDetails, index) in chatContents"
      ref="refsText"
      :key="getKey(messageDetails)"
      :class="['translated-text', getClassTextTarget(messageDetails, index)]"
      :dir="isRightToLeft ? 'rtl' : 'ltr'"
      :style="styleParagraph"
      :is-last-sub-id="index === chatContents.length - 1"
      :message-details
      @edit="openModalEdit"
    />
    <Teleport to="body">
      <ModalBig title="원문 수정" v-if="isVisibleModalEdit" @close="closeModalEdit">
        <GeneralMessageCommonModalEdit :close-modal-edit :message-details :data-flag="'tgt'" :language-code-set />
      </ModalBig>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, Ref, ref, useTemplateRef, watch, CSSProperties } from 'vue';

import GeneralMessageChatText from '@/components/domains/chat/chat-general/GeneralMessageChatText.vue';
import GeneralMessageCommonModalEdit from '@/components/domains/chat/chat-general/GeneralMessageCommonModalEdit.vue';
import ModalBig from '@/components/shared/composite/ModalBig.vue';
import { useMessageTextCommon } from '@/composables/domains/chat/useMessageTextCommon';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { MessageText } from '@/types/socket';
import { isLanguageNoSpace } from '@/utils/chat/language';
import { ij_isLastIsOther, ij_isLastMessage, ij_isOldMessage } from '@/utils/injectionKeys.js';

const userStore = useUserStore();
const socketStore = useSocketStore();

const props = withDefaults(
  defineProps<{
    chatContents: MessageText[];
    isTargetPlaying: boolean;
    isComplete: boolean | undefined;
    isInterpreting: boolean;
    languageCodeSet: Set<LanguageCode>;
    usingAutoHeight: boolean;
    isDmMessage: boolean;
  }>(),
  {
    chatContents: () => [],
    isTargetPlaying: false,
    isComplete: false,
    isInterpreting: false,
    languageCodeSet: new Set(),
    usingAutoHeight: true,
    isDmMessage: false,
  }
);

const isLastIsOther = inject(ij_isLastIsOther) as Ref<boolean>; // 이 메세지가 마지막인지
const isLastMessage = inject(ij_isLastMessage) as Ref<boolean>; // 이 메세지가 마지막인지
const isOldMessage = inject(ij_isOldMessage) as Ref<boolean>; // 이 메세지가 마지막인지

const contentContainerTgt = ref<HTMLElement>();
const refsText = useTemplateRef('refsText');
const messageDetails = ref({}) as Ref<MessageText>;
const isVisibleModalEdit = ref(false); // 문구 수정 모달이 보이는지

// 오른쪽 정렬 언어인지 반환
const isRightToLeft = computed(() => {
  const listLangAlignRight: LanguageCode[] = ['ar']; // 아랍어,
  return listLangAlignRight.some((lang) => props.languageCodeSet.has(lang));
});

// 글자 크기 배율
const fontMultiplier = computed(() => userStore.userPreferences.textSizeMultiplier);

// 줄어들어야 할 대상인지
const isShrinkTarget = computed(() => {
  if (isLastMessage.value) return false;
  return isOldMessage.value || (!isLastMessage.value && props.isComplete);
});

const styleParagraph = computed((): CSSProperties => {
  return {
    wordBreak: isEveryLanguageSpaced.value ? 'keep-all' : 'break-all',
  };
});

const isEveryLanguageSpaced = computed(() => {
  return [...props.languageCodeSet].every((code) => !isLanguageNoSpace(code));
});

// 한 줄의 높이
const getlineHeight = () => {
  const el = refsText.value?.[0]?.refTextSubId;
  if (!el) return 16 * 1.5;

  const style = getComputedStyle(el);
  return parseFloat(style.fontSize) * 1.5;
};

// 문장별 클래스
const getClassTextTarget = (content: MessageText, index: number) => {
  const isLastPart = index === props.chatContents.length - 1;

  return {
    'sound-playing': props.isTargetPlaying,
    'highlight-translated': isLastIsOther.value && isLastPart && content.highlight,
    'highlight-translated-fadeout': isLastIsOther.value && !!content.fadeout,
  };
};

// 수정 모달 닫기
const closeModalEdit = () => {
  isVisibleModalEdit.value = false;
};

// 수정 모달 띄우기
const openModalEdit = (messageClicked: MessageText) => {
  if (!userStore.userInfo.isTester) return;
  if (socketStore.connectInfo!.connectionType !== 'GENERAL') return;
  const isEditable = messageClicked.isEditable || !isLastIsOther.value; // 수정 가능 여부 (확정되거나 더이상 마지막 메세지가 아닌 경우 가능)
  if (!isEditable) return;
  messageDetails.value = messageClicked;
  isVisibleModalEdit.value = true;
};

const { updateContainerHeight, shrinkBubble, getKey } = useMessageTextCommon({
  contentContainer: contentContainerTgt,
});

onMounted(() => {
  if (props.isDmMessage) {
    return;
  }
  if (isLastIsOther.value && props.usingAutoHeight && !isShrinkTarget.value) {
    updateContainerHeight(getlineHeight());
  }
});

// 내용이 바뀔 때마다 버블 높이 조절
watch(
  () => props.chatContents,
  () => {
    if (props.isDmMessage) {
      return;
    }
    if (isLastIsOther.value && props.usingAutoHeight) {
      updateContainerHeight(getlineHeight());
    }
  },
  { flush: 'post' }
);

// 글자 크기 변경 시 높이 조절
watch(
  fontMultiplier,
  () => shrinkBubble() // 버블 줄이기
);
</script>

<style scoped lang="scss">
.content-container {
  overflow: hidden;
  min-height: 1px;
  height: auto;
  padding: 0;
  transition:
    height 0.7s ease,
    min-height 0.7s ease;
  text-align: start;
}

/* 통역본 - 텍스트 */
.translated-text {
  display: inline;
  line-height: 160%;
  color: var(--font-darkest);
  font-size: calc($font-size-p * var(--font-multiplier, 1));
  unicode-bidi: isolate;
}

/* 통역본 - 소리 Play */
.sound-playing {
  color: var(--font-darkest);
}

/* 통역본 - 통역/입력중인 텍스트 */
.highlight-translated {
  display: inline;
  color: var(--font-darkest);
  animation: none;
  opacity: 0.5;
}

/* 통역본 - 통역 완료된 텍스트 */
.highlight-translated-fadeout {
  display: inline;
  color: var(--font-darkest);
  animation: fadeIn 3s ease-in-out forwards;
}

@keyframes fadeIn {
  0% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}
</style>
